import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api.js';
import BookingGroupRow from '../../components/BookingGroups.jsx';
import { groupByBooking, useCollapsedGroups } from '../../utils/bookingGroups.js';
import PortConditionsPanel from '../../components/PortConditionsPanel.jsx';
import ConfirmModal from '../../components/ConfirmModal.jsx';
import { dateTime24 } from '../../utils/time24.js';
import './dispatch-planning.scss';

// 8926: affiliate broadcasting is hidden for now; flip this to bring it back.
const SHOW_AFFILIATE_BROADCAST = false;

const CAPTAIN_SOURCE = { roster: 'rostered on this tug that day', tug: 'assigned to this tug' };

// 8940: badge colour for where a ticket is in its life.
const TICKET_STATUS_BADGE = {
  Sent: 'bg-info text-dark',
  Acknowledged: 'bg-primary',
  'No Response': 'bg-danger',
  'In Progress': 'bg-warning text-dark',
  Completed: 'bg-success',
  Cancelled: 'bg-secondary',
};
// Once the tug is working the job there is nothing left to acknowledge or reassign.
const ticketOpen = (t) => !['In Progress', 'Completed', 'Cancelled'].includes(t.ticket_status);

/** A Date as the value a datetime-local input expects (local time). */
function toLocalInput(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

// 9032: times on Plan & Dispatch read on the 24-hour clock.
const fmtWhen = dateTime24;

// 9029: whether the ticket's captain was emailed.
function emailStatusText(t) {
  switch (t.captain_email_status) {
    case 'sent':
      return `Emailed to ${t.captain_email_to}`;
    case 'simulated':
      return `Email logged (mail server not set up) — ${t.captain_email_to}`;
    case 'failed':
      return `Email failed${t.captain_email_to ? ` — ${t.captain_email_to}` : ''}`;
    case 'no_email':
      return 'Not emailed — no email on file';
    default:
      return 'Email pending…';
  }
}

// 9029: the email goes out moments after dispatch, so a ticket with no outcome is only
// "pending" while fresh; older ones were dispatched before captain emails existed.
const EMAIL_PENDING_MS = 10 * 60 * 1000;
const showEmailStatus = (t) =>
  Boolean(t.captain_email_status) || (t.sent_at && Date.now() - new Date(t.sent_at).getTime() < EMAIL_PENDING_MS);

// 9033: a job the dispatcher may still cancel, or whose tug may still be replaced.
const ticketCancellable = (t) =>
  Boolean(t.booking_job_id) && !t.sof_submitted && !['Completed', 'Cancelled'].includes(t.ticket_status);
const ticketReplaceable = (t) => Boolean(t.booking_job_id) && !t.sof_submitted && ticketOpen(t);
const bookingOpen = (status) => ['Confirmed', 'Planned'].includes(status);
const jobTypeOf = (name) => String(name || '').replace(/ — Tug \d+$/, '');

export default function DispatchPlanningPage() {
  const [jobs, setJobs] = useState([]); // 8952: the Confirmed Job Queue
  // 8986 / 8989: the queue and the tickets are grouped by booking; each group folds away.
  const [isQueueCollapsed, toggleQueueGroup] = useCollapsedGroups();
  const [isTicketCollapsed, toggleTicketGroup] = useCollapsedGroups();
  const [tugs, setTugs] = useState([]);
  const [captains, setCaptains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [pageMsg, setPageMsg] = useState(null);

  // 8953: the job on the planning desk and what the planner has chosen for it.
  const [selected, setSelected] = useState(null);
  const [planning, setPlanning] = useState(null); // advisory + availability at the chosen time
  const [forecast, setForecast] = useState(null); // FR-SCH-4 capacity forecast
  const [tugId, setTugId] = useState('');
  const [captainId, setCaptainId] = useState('');
  const [captainAuto, setCaptainAuto] = useState(false); // filled in from the Crew Roster
  const [scheduledAt, setScheduledAt] = useState(''); // datetime-local value
  const [override, setOverride] = useState(false);
  const [justification, setJustification] = useState('');
  const [planError, setPlanError] = useState('');
  const [planMsg, setPlanMsg] = useState('');
  const [dispatching, setDispatching] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [addingJob, setAddingJob] = useState(false);
  // 9027: the job summary confirmed before the ticket is sent.
  const [summaryOpen, setSummaryOpen] = useState(false);

  // 9033: Add Tug / Replace Tug / Cancel Job.
  const [addTugTarget, setAddTugTarget] = useState(null); // { bookingId, bookingRef, jobs: [{id,label}] }
  const [addTugLike, setAddTugLike] = useState('');
  const [addTugReason, setAddTugReason] = useState('');
  const [addTugError, setAddTugError] = useState('');
  const [replaceTarget, setReplaceTarget] = useState(null); // a ticket row
  const [replaceOptions, setReplaceOptions] = useState(null);
  const [replaceTugId, setReplaceTugId] = useState('');
  const [replaceCaptainId, setReplaceCaptainId] = useState('');
  const [replaceReason, setReplaceReason] = useState('');
  const [replaceError, setReplaceError] = useState('');
  const [cancelTarget, setCancelTarget] = useState(null); // { jobId, jobNo, jobName, bookingRef, ticketNo }
  const [cancelReason, setCancelReason] = useState('');
  const [cancelError, setCancelError] = useState('');
  const [jobActionBusy, setJobActionBusy] = useState(false);

  // Job-ticket monitoring + reassignment.
  const [tickets, setTickets] = useState([]);
  const [reassignTarget, setReassignTarget] = useState(null);
  const [reassignTug, setReassignTug] = useState('');
  const [reassignReason, setReassignReason] = useState('');
  const [reassignError, setReassignError] = useState('');
  const [reassigning, setReassigning] = useState(false);
  const [ticketMsg, setTicketMsg] = useState(null);
  // Recording the ship's master's confirmation on their behalf.
  const [masterTarget, setMasterTarget] = useState(null);
  const [masterName, setMasterName] = useState('');
  const [masterNote, setMasterNote] = useState('');
  const [masterError, setMasterError] = useState('');
  const [masterSaving, setMasterSaving] = useState(false);

  // Affiliate capacity broadcast.
  const [broadcastBooking, setBroadcastBooking] = useState(null);
  const [eligible, setEligible] = useState(null);
  const [selectedOperators, setSelectedOperators] = useState([]);
  const [broadcasting, setBroadcasting] = useState(false);
  const [broadcastError, setBroadcastError] = useState('');

  const openBroadcast = async (booking) => {
    setBroadcastError('');
    setSelectedOperators([]);
    setEligible(null);
    setBroadcastBooking(booking);
    try {
      const e = await api.getBroadcastEligible(booking.id);
      setEligible(e);
      // Pre-select all eligible operators by default.
      setSelectedOperators((e.operators || []).map((o) => o.operatorId));
    } catch (err) {
      setBroadcastError(err.message || 'Failed to load eligible affiliates.');
    }
  };

  const toggleOperator = (id) =>
    setSelectedOperators((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const submitBroadcast = async (e) => {
    e.preventDefault();
    setBroadcastError('');
    if (selectedOperators.length === 0) {
      setBroadcastError('Select at least one affiliate operator.');
      return;
    }
    setBroadcasting(true);
    try {
      const res = await api.createBroadcast({
        bookingId: broadcastBooking.id,
        operatorIds: selectedOperators,
      });
      setBroadcastBooking(null);
      setPageMsg({
        type: 'success',
        text: `Broadcast ${res.broadcastRef} sent to ${res.targetCount} affiliate(s) via ${res.notifiedVia} — status ${res.status}.`,
      });
    } catch (err) {
      setBroadcastError(err.message || 'Failed to broadcast.');
    } finally {
      setBroadcasting(false);
    }
  };

  const load = useCallback(async () => {
    const [jobsRes, tugsRes, membersRes, ticketsRes, forecastRes] = await Promise.all([
      api.listJobQueue(),
      api.listTugs(),
      api.listCrewMembers(),
      api.listJobTickets(),
      api.getCapacityForecast().catch(() => null),
    ]);
    setJobs(jobsRes || []);
    setTugs(tugsRes || []);
    setCaptains((membersRes || []).filter((m) => m.position === 'Captain'));
    setTickets(ticketsRes || []);
    setForecast(forecastRes || null);
  }, []);

  const openReassign = (ticket) => {
    setReassignError('');
    setReassignTug('');
    setReassignReason('');
    setReassignTarget(ticket);
  };

  // The captain acknowledged another way (radio, phone): the office records it.
  const markAcknowledged = async (ticket) => {
    setTicketMsg(null);
    try {
      await api.acknowledgeJob(ticket.id);
      setTicketMsg({ type: 'success', text: `Ticket ${ticket.ticket_no ?? ''} marked acknowledged on the captain's behalf.` });
      await load();
    } catch (err) {
      setTicketMsg({ type: 'danger', text: err.message || 'Failed to mark the ticket acknowledged.' });
    }
  };

  const openMaster = (ticket) => {
    setMasterError('');
    setMasterName('');
    setMasterNote('');
    setMasterTarget(ticket);
  };

  const submitMaster = async (e) => {
    e.preventDefault();
    setMasterError('');
    if (masterName.trim().length < 2) {
      setMasterError("Enter the name of the ship's master or agent.");
      return;
    }
    if (masterNote.trim().length < 3) {
      setMasterError('Say how the confirmation was received.');
      return;
    }
    setMasterSaving(true);
    try {
      await api.confirmAssistForMaster(masterTarget.id, { name: masterName.trim(), note: masterNote.trim() });
      setTicketMsg({ type: 'success', text: `Assist confirmation recorded for ${masterTarget.booking_ref}${masterTarget.job_no ? ` ${masterTarget.job_no}` : ''} (${masterTarget.ticket_no}).` });
      setMasterTarget(null);
      await load();
    } catch (err) {
      setMasterError(err.details?.[0]?.message || err.message || 'Failed to record the confirmation.');
    } finally {
      setMasterSaving(false);
    }
  };

  const submitReassign = async (e) => {
    e.preventDefault();
    setReassignError('');
    if (!reassignTug) {
      setReassignError('Select an alternative tug.');
      return;
    }
    if (!reassignReason.trim()) {
      setReassignError('A reassignment rationale is required.');
      return;
    }
    setReassigning(true);
    try {
      await api.reassignJob(reassignTarget.id, { toTugId: reassignTug, reason: reassignReason.trim() });
      setTicketMsg({ type: 'success', text: 'Job reassigned — rationale logged to the audit trail.' });
      setReassignTarget(null);
      await load();
    } catch (err) {
      setReassignError(err.message || 'Failed to reassign.');
    } finally {
      setReassigning(false);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        await load();
      } catch (err) {
        setLoadError(err.message || 'Failed to load dispatch planning.');
      } finally {
        setLoading(false);
      }
    })();
  }, [load]);

  // Captains who may be picked at the job's time (certified, not on an overlapping job).
  const assignableCaptains = useMemo(() => {
    const byId = new Map((planning?.captains || []).map((c) => [c.id, c]));
    return captains.filter((c) =>
      planning ? byId.get(c.id)?.available === true : !(c.compliance.applicable && !c.compliance.compliant)
    );
  }, [captains, planning]);

  const availability = useMemo(
    () => Object.fromEntries((planning?.tugs || []).map((t) => [t.id, t])),
    [planning]
  );

  // 8925 / 8953: the captain the Crew Roster puts on a tug that day, if they can go.
  const rosterCaptainFor = (id, ctx = planning) => {
    const own = (ctx?.tugs || []).find((t) => t.id === id)?.captain;
    const free = (ctx?.captains || []).find((c) => c.id === own?.id)?.available;
    return own && own.available && free ? own : null;
  };

  /** Load the desk for a job; `at` (ISO) re-checks availability for an edited time. */
  const loadPlanning = async (job, at) => {
    const ctx = await api.getJobPlanning(job.id, at);
    setPlanning(ctx);
    return ctx;
  };

  const planJob = async (job) => {
    setPageMsg(null);
    setPlanError('');
    setPlanMsg('');
    setTugId('');
    setCaptainId('');
    setCaptainAuto(false);
    setOverride(false);
    setJustification('');
    setSelected(job);
    setPlanning(null);
    setScheduledAt(toLocalInput(job.scheduled_at));
    try {
      const ctx = await loadPlanning(job);
      // 8924: pick up where a saved assignment left off.
      if (ctx.saved) {
        setTugId(ctx.saved.tugId || '');
        setCaptainId(ctx.saved.captainId || '');
        if (ctx.saved.overrideJustification) {
          setOverride(true);
          setJustification(ctx.saved.overrideJustification);
        }
        setPlanMsg(`Saved assignment loaded${ctx.saved.savedBy ? ` (saved by ${ctx.saved.savedBy})` : ''}.`);
      }
    } catch (err) {
      setPlanError(err.message || 'Failed to load the job.');
    }
  };

  // 8953: picking the job's one tug fills in the captain the roster has on it.
  const changeTug = (id) => {
    setTugId(id);
    setPlanError('');
    const own = rosterCaptainFor(id);
    if (own) {
      setCaptainId(own.id);
      setCaptainAuto(true);
    } else if (captainAuto || !captainId) {
      setCaptainId('');
      setCaptainAuto(false);
    }
  };

  // 8953: a new time can change which tugs and captains are free.
  const changeSchedule = async (value) => {
    setScheduledAt(value);
    setPlanError('');
    if (!selected || !value || Number.isNaN(new Date(value).getTime())) return;
    try {
      const ctx = await loadPlanning(selected, new Date(value).toISOString());
      const verdict = ctx.tugs.find((t) => t.id === tugId);
      if (tugId && verdict && !verdict.available) {
        setTugId('');
        setCaptainId('');
        setCaptainAuto(false);
        setPlanMsg(`${verdict.name} is not free at the new time (${verdict.unavailableReason}) — pick another tug.`);
      } else if (tugId && captainAuto) {
        const own = rosterCaptainFor(tugId, ctx);
        setCaptainId(own ? own.id : '');
        setCaptainAuto(Boolean(own));
      }
    } catch (err) {
      setPlanError(err.message || 'Failed to check availability for that time.');
    }
  };

  const assignment = () => ({
    tugId: tugId || null,
    captainId: captainId || null,
    scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : undefined,
    override,
    overrideJustification: justification.trim() || null,
  });

  const validSchedule = () => {
    if (!scheduledAt || Number.isNaN(new Date(scheduledAt).getTime())) {
      setPlanError('Enter the job’s scheduled date and time.');
      return false;
    }
    return true;
  };

  // 8924 / 8953: keep the job's tug, captain and time without issuing its ticket.
  const saveAssignment = async () => {
    setPlanError('');
    setPlanMsg('');
    if (!tugId) {
      setPlanError('Select the tug for this job before saving.');
      return;
    }
    if (!validSchedule()) return;
    setSavingDraft(true);
    try {
      await api.saveJobPlan(selected.id, assignment());
      setPlanMsg(`Assignment saved for job ${selected.job_no}. No job ticket was sent; trigger dispatch when ready.`);
      setJobs(await api.listJobQueue());
    } catch (err) {
      setPlanError(err.details?.[0]?.message || err.message || 'Failed to save the assignment.');
    } finally {
      setSavingDraft(false);
    }
  };

  // Advisory problems for this job with the chosen tug (the server checks again).
  const warnings = useMemo(() => {
    if (!planning) return [];
    const list = [...(planning.warnings || [])];
    const tug = tugs.find((t) => t.id === tugId);
    const min = planning.advisory?.minBollardPullTons;
    if (tug && min != null && Number(tug.bollard_pull_tons) < min) {
      list.push(`${tug.name} (${Number(tug.bollard_pull_tons)}T) is below the ${min}T minimum.`);
    }
    return list;
  }, [planning, tugId, tugs]);

  const shortOfTugs =
    planning?.advisory?.recommendedTugCount != null && planning.sameTypeCount < planning.advisory.recommendedTugCount;

  // One more job (one more tug) on the booking, when the advisory needs it.
  const addTugJob = async () => {
    setPlanError('');
    setAddingJob(true);
    try {
      const added = await api.addBookingJob(selected.booking_id, { jobName: planning.jobType });
      setPlanMsg(`Job ${added.job_no} (${added.job_name}) added to ${selected.booking_ref} — plan it from the queue.`);
      const [queue] = await Promise.all([api.listJobQueue(), loadPlanning(selected, assignment().scheduledAt)]);
      setJobs(queue);
    } catch (err) {
      setPlanError(err.message || 'Failed to add a job.');
    } finally {
      setAddingJob(false);
    }
  };

  const dispatch = async (e) => {
    e.preventDefault();
    setPlanError('');
    if (!tugId) {
      setPlanError('Select the tug for this job.');
      return;
    }
    if (!validSchedule()) return;
    if (warnings.length > 0 && (!override || !justification.trim())) {
      setPlanError('Advisory not met — enable override and provide a justification to proceed.');
      return;
    }
    // 9027: show the job summary; the ticket is sent once it is confirmed.
    setSummaryOpen(true);
  };

  const confirmDispatch = async () => {
    setDispatching(true);
    try {
      const res = await api.dispatchJob(selected.id, assignment());
      setSummaryOpen(false);
      setPageMsg({
        type: 'success',
        text: `Dispatched job ticket No. ${res.ticket.ticket_no} for job ${res.jobNo} (${selected.job_name}, ${selected.booking_ref})${
          res.overridden ? ' — advisory overridden' : ''
        }.`,
      });
      setSelected(null);
      setPlanning(null);
      await load();
    } catch (err) {
      setSummaryOpen(false);
      setPlanError(err.message || 'Failed to dispatch.');
    } finally {
      setDispatching(false);
    }
  };

  // 9027: what the dispatcher confirms — the booking, where and when, and each
  // of its jobs with its tug and captain (this job with the ones just picked).
  const summaryJobs = useMemo(() => {
    if (!selected) return [];
    const tugName = tugs.find((t) => t.id === tugId)?.name || '—';
    const captainName = captains.find((c) => c.id === captainId)?.name || null;
    const rows = planning?.bookingJobs?.length ? planning.bookingJobs : [selected];
    return rows.map((j) =>
      j.id === selected.id
        ? { ...j, current: true, tug_name: tugName, captain_name: captainName, status: 'To dispatch now' }
        : { ...j, current: false, status: j.status === 'Saved' ? 'Assignment saved' : j.status }
    );
  }, [selected, planning, tugs, captains, tugId, captainId]);

  // ---- 9033: Add Tug / Replace Tug / Cancel Job ----

  /** The booking's live jobs (queued and dispatched), to pick which one to add a tug like. */
  const bookingJobOptions = (bookingId) => {
    const seen = new Map();
    for (const j of jobs.filter((x) => x.booking_id === bookingId)) {
      seen.set(j.id, { id: j.id, label: `${j.job_no} — ${j.job_name}`, type: jobTypeOf(j.job_name) });
    }
    for (const t of tickets.filter((x) => x.booking_id === bookingId && x.booking_job_id)) {
      if (t.ticket_status === 'Cancelled' || seen.has(t.booking_job_id)) continue;
      seen.set(t.booking_job_id, { id: t.booking_job_id, label: `${t.job_no} — ${t.job_name}`, type: jobTypeOf(t.job_name) });
    }
    // One entry per job type is enough: the new job copies its type.
    const byType = new Map();
    for (const o of seen.values()) if (!byType.has(o.type)) byType.set(o.type, o);
    return [...byType.values()];
  };

  const openAddTug = (bookingId, bookingRef) => {
    const options = bookingJobOptions(bookingId);
    setAddTugError('');
    setAddTugReason('');
    setAddTugLike(options[0]?.id || '');
    setAddTugTarget({ bookingId, bookingRef, jobs: options });
  };

  const submitAddTug = async () => {
    setAddTugError('');
    if (!addTugLike) {
      setAddTugError('Select the job the extra tug is for.');
      return;
    }
    setJobActionBusy(true);
    try {
      const added = await api.addBookingJob(addTugTarget.bookingId, {
        likeJobId: addTugLike,
        ...(addTugReason.trim() ? { reason: addTugReason.trim() } : {}),
      });
      setPageMsg({
        type: 'success',
        text: `Job ${added.job_no} (${added.job_name}) added to ${addTugTarget.bookingRef} — plan and dispatch it from the Confirmed Job Queue.`,
      });
      setAddTugTarget(null);
      await load();
    } catch (err) {
      setAddTugError(err.details?.[0]?.message || err.message || 'Failed to add a tug.');
    } finally {
      setJobActionBusy(false);
    }
  };

  const openReplace = async (ticket) => {
    setReplaceError('');
    setReplaceTugId('');
    setReplaceCaptainId('');
    setReplaceReason('');
    setReplaceOptions(null);
    setReplaceTarget(ticket);
    try {
      setReplaceOptions(await api.getReplacementOptions(ticket.booking_job_id));
    } catch (err) {
      setReplaceError(err.message || 'Failed to load the tugs available for this job.');
    }
  };

  // Picking the replacement tug fills in the captain the Crew Roster has on it.
  const changeReplaceTug = (id) => {
    setReplaceTugId(id);
    const tug = (replaceOptions?.tugs || []).find((t) => t.id === id);
    const own = tug?.captain;
    const free = (replaceOptions?.captains || []).find((c) => c.id === own?.id)?.available;
    setReplaceCaptainId(own && own.available && free ? own.id : '');
  };

  const submitReplace = async () => {
    setReplaceError('');
    if (!replaceTugId) {
      setReplaceError('Select the replacement tug.');
      return;
    }
    if (replaceReason.trim().length < 3) {
      setReplaceError('Say why the tug is being replaced.');
      return;
    }
    setJobActionBusy(true);
    try {
      const res = await api.replaceJobTug(replaceTarget.booking_job_id, {
        tugId: replaceTugId,
        captainId: replaceCaptainId || null,
        reason: replaceReason.trim(),
      });
      setTicketMsg({
        type: 'success',
        text: `${res.jobNo} now goes to ${res.tugName}${res.captainName ? ` with ${res.captainName}` : ''}${
          res.captainChanged && res.captainName ? ' — the captain is emailed the ticket' : ''
        }. Logged to the audit trail.`,
      });
      setReplaceTarget(null);
      await load();
    } catch (err) {
      setReplaceError(err.details?.[0]?.message || err.message || 'Failed to replace the tug.');
    } finally {
      setJobActionBusy(false);
    }
  };

  const openCancel = (job) => {
    setCancelError('');
    setCancelReason('');
    setCancelTarget(job);
  };

  const submitCancel = async () => {
    setCancelError('');
    if (cancelReason.trim().length < 3) {
      setCancelError('A cancellation reason is required.');
      return;
    }
    setJobActionBusy(true);
    try {
      const res = await api.cancelBookingJob(cancelTarget.jobId, { reason: cancelReason.trim() });
      setPageMsg({
        type: 'success',
        text: `Job ${res.jobNo} of ${res.bookingRef} cancelled — it will not be billed. Logged to the audit trail.`,
      });
      if (selected?.id === cancelTarget.jobId) {
        setSelected(null);
        setPlanning(null);
      }
      setCancelTarget(null);
      await load();
    } catch (err) {
      setCancelError(err.details?.[0]?.message || err.message || 'Failed to cancel the job.');
    } finally {
      setJobActionBusy(false);
    }
  };

  const advisory = planning?.advisory;
  const ownCaptain = tugId ? availability[tugId]?.captain : null;

  return (
    <div className="container-fluid py-4" data-testid="dispatch-page">
      <PortConditionsPanel />
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm">
            <div>
              <h3 className="text-primary mb-0">
                <i className="fas fa-route me-2" aria-hidden="true" />
                Dispatch Planning &amp; Allocation
              </h3>
              <p className="text-muted mb-0 small">
                Plan each job of a confirmed booking — one tug per job — with its captain and time, then dispatch its
                ticket.
              </p>
            </div>
            <span className="badge bg-primary p-2">Roster Integration: Active</span>
          </div>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-danger" data-testid="dispatch-load-error">
          {loadError}
        </div>
      )}
      {pageMsg && (
        <div className={`alert alert-${pageMsg.type}`} role="alert" data-testid="dispatch-message">
          {pageMsg.text}
        </div>
      )}

      {/* Capacity forecast → affiliate-needed (FR-SCH-4) */}
      {forecast && forecast.rows.length > 0 && (
        <div className="card border-0 shadow-sm mb-4" data-testid="capacity-forecast">
          <div className="card-header bg-white pt-3 border-bottom-0 d-flex justify-content-between align-items-center">
            <h6 className="mb-0 text-secondary">
              <i className="fas fa-chart-line me-2" aria-hidden="true" />
              Capacity Forecast ({forecast.days}d) · owned fleet {forecast.fleet}
            </h6>
            {forecast.affiliateNeededDays > 0 ? (
              <span className="badge bg-danger" data-testid="affiliate-needed">
                <i className="fas fa-triangle-exclamation me-1" aria-hidden="true" />
                Affiliate needed: {forecast.affiliateNeededDays} day(s)
              </span>
            ) : (
              <span className="badge bg-success">Owned capacity sufficient</span>
            )}
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0 small" data-testid="forecast-table">
                <thead className="bg-light text-secondary text-uppercase">
                  <tr><th>Date</th><th className="text-center">Demand (tugs)</th><th className="text-center">Capacity</th><th className="text-center">Shortfall</th><th>Signal</th></tr>
                </thead>
                <tbody>
                  {forecast.rows.map((r) => (
                    <tr key={r.date} data-testid="forecast-row" className={r.affiliateNeeded ? 'table-danger' : ''}>
                      <td className="fw-semibold">{r.date}</td>
                      <td className="text-center">{r.demand}</td>
                      <td className="text-center">{r.capacity}</td>
                      <td className="text-center">{r.shortfall || '—'}</td>
                      <td>
                        {r.affiliateNeeded ? (
                          <span className="badge bg-danger">Broadcast to affiliates</span>
                        ) : (
                          <span className="text-success small">OK</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : (
        <div className="row">
          {/* LEFT: 8952 — Confirmed Job Queue, one row per job */}
          <div className="col-lg-6 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-clipboard-list me-2" aria-hidden="true" />
                  Confirmed Job Queue
                </h5>
                <p className="small text-muted mb-0">Each job is done by one tug and is planned and dispatched on its own.</p>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0" data-testid="job-queue">
                    <thead className="bg-light text-secondary small text-uppercase">
                      <tr>
                        <th>Job</th>
                        <th>Scheduled</th>
                        <th aria-label="action" />
                      </tr>
                    </thead>
                    <tbody>
                      {jobs.length === 0 && (
                        <tr>
                          <td colSpan={3} className="text-center text-muted py-4">
                            No confirmed jobs awaiting planning.
                          </td>
                        </tr>
                      )}
                      {/* 8986: the booking once, with its jobs awaiting planning beneath it */}
                      {groupByBooking(jobs).map((g) => {
                        const b = g.first;
                        const collapsed = isQueueCollapsed(g.key);
                        return (
                          <React.Fragment key={g.key}>
                            <BookingGroupRow group={g} colSpan={3} collapsed={collapsed} onToggle={toggleQueueGroup}>
                              <span data-testid="booking-group-detail">
                                {b.vessel_name || '—'} · {b.service_name || b.movement_type} · {b.port_name || '—'}
                                {b.assistance_area_name ? ` (${b.assistance_area_name})` : ''}
                                {b.trading_status ? ` · ${b.trading_status}` : ''}
                                {b.booking_job_count > g.rows.length
                                  ? ` · ${b.booking_job_count - g.rows.length} of ${b.booking_job_count} jobs already dispatched`
                                  : ''}
                              </span>
                              {/* 9033: one more tug (job) on the booking */}
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary ms-2 py-0"
                                onClick={() => openAddTug(b.booking_id, b.booking_ref)}
                                data-testid="queue-add-tug"
                              >
                                <i className="fas fa-plus me-1" aria-hidden="true" />
                                Add Tug
                              </button>
                              {SHOW_AFFILIATE_BROADCAST && (
                                <button
                                  className="btn btn-sm btn-outline-secondary ms-2"
                                  onClick={() => openBroadcast({ id: b.booking_id, booking_ref: b.booking_ref })}
                                  data-testid="broadcast-affiliates"
                                >
                                  <i className="fas fa-satellite-dish me-1" aria-hidden="true" />
                                  Broadcast to Affiliates
                                </button>
                              )}
                            </BookingGroupRow>
                            {!collapsed &&
                              g.rows.map((j) => (
                                <tr
                                  key={j.id}
                                  className={selected?.id === j.id ? 'table-primary' : ''}
                                  data-testid="confirmed-row"
                                  data-ref={j.booking_ref}
                                  data-job-no={j.job_no}
                                >
                                  <td className="ps-4">
                                    <span className="fw-bold d-block" data-testid="queue-job-name">
                                      {j.job_name}
                                    </span>
                                    <small className="text-muted" data-testid="queue-job-no">
                                      {j.job_no} · job {j.seq} of {j.booking_job_count}
                                    </small>
                                    {j.has_draft && (
                                      <span className="badge bg-info text-dark d-block mt-1 fw-normal" data-testid="draft-badge">
                                        Assignment saved{j.planned_tug_name ? `: ${j.planned_tug_name}` : ''}
                                      </span>
                                    )}
                                  </td>
                                  <td className="small" data-testid="queue-job-time">
                                    {fmtWhen(j.scheduled_at)}
                                  </td>
                                  <td className="text-end text-nowrap">
                                    <button
                                      className="btn btn-sm btn-primary"
                                      onClick={() => planJob(j)}
                                      data-testid="plan-resources"
                                    >
                                      <i className="fas fa-sliders-h me-1" aria-hidden="true" />
                                      Plan &amp; Dispatch
                                    </button>
                                    {/* 9033: a job the booking no longer needs */}
                                    <button
                                      className="btn btn-sm btn-outline-danger ms-1"
                                      onClick={() =>
                                        openCancel({
                                          jobId: j.id,
                                          jobNo: j.job_no,
                                          jobName: j.job_name,
                                          bookingRef: j.booking_ref,
                                          ticketNo: null,
                                        })
                                      }
                                      title="Cancel this job"
                                      data-testid="queue-cancel-job"
                                    >
                                      <i className="fas fa-ban" aria-hidden="true" />
                                      <span className="visually-hidden">Cancel Job</span>
                                    </button>
                                  </td>
                                </tr>
                              ))}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: 8953 — planning desk for one job */}
          <div className="col-lg-6 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-sliders-h me-2" aria-hidden="true" />
                  Resource Planning Desk
                </h5>
              </div>
              <div className="card-body">
                {!selected ? (
                  <div className="text-center text-muted py-5 my-4" data-testid="no-booking">
                    <i className="fas fa-anchor fa-3x mb-3 text-secondary" aria-hidden="true" />
                    <h6>No job selected</h6>
                    <p className="small">Click “Plan &amp; Dispatch” on a job in the queue.</p>
                  </div>
                ) : (
                  <form onSubmit={dispatch} noValidate data-testid="planning-area">
                    {/* 8886 / 8955: which booking, service and job is being planned. */}
                    <div className="border rounded p-2 mb-3 bg-light small" data-testid="selected-booking">
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Booking</span>
                        <strong data-testid="selected-booking-ref">{selected.booking_ref}</strong>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Vessel</span>
                        <strong data-testid="selected-vessel-name">{selected.vessel_name || '—'}</strong>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Service</span>
                        <strong>{selected.service_name || selected.movement_type}</strong>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Job</span>
                        <strong data-testid="selected-job">
                          {selected.job_no} — {selected.job_name}
                        </strong>
                      </div>
                      <div className="text-muted">
                        {selected.berth_name || selected.port_name || '—'}
                        {selected.assistance_area_name ? ` · ${selected.assistance_area_name}` : ''}
                      </div>
                    </div>
                    {advisory && (
                      <div className="alert alert-info p-3 rounded small mb-3" data-testid="advisory">
                        <h6 className="fw-bold mb-1 text-dark">
                          <i className="fas fa-shield-alt me-1" aria-hidden="true" />
                          PPA Advisory Recommendation
                        </h6>
                        {advisory.recommendedTugCount != null && advisory.minBollardPullTons != null ? (
                          <p className="mb-0">
                            Vessel (GT {advisory.vesselGt ?? '—'}) requires{' '}
                            <strong data-testid="recommended-tugs">{advisory.recommendedTugCount}</strong> tug(s)
                            with a minimum{' '}
                            <strong data-testid="min-bollard">{advisory.minBollardPullTons}</strong>-Ton bollard
                            pull{advisory.compulsory ? ' (PPA compulsory floor).' : '.'} {planning.jobType} has{' '}
                            <strong data-testid="job-type-count">{planning.sameTypeCount}</strong> tug job(s) on this
                            booking.
                          </p>
                        ) : (
                          // Name the gap instead of printing a bare dash (8852).
                          <p className="mb-0 text-danger" data-testid="advisory-incomplete">
                            {advisory.vesselGt == null
                              ? 'The vessel has no gross tonnage on record, so no tug count or minimum bollard pull can be advised.'
                              : advisory.recommendedTugCount == null
                                ? `No allocation rule covers GT ${advisory.vesselGt}, so no tug count or minimum bollard pull can be advised. Add one under Allocation Rules.`
                                : `The allocation rule for GT ${advisory.vesselGt} requires ${advisory.recommendedTugCount} tug(s) but sets no minimum bollard pull. Add one under Allocation Rules.`}
                          </p>
                        )}
                        {shortOfTugs && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary mt-2"
                            onClick={addTugJob}
                            disabled={addingJob}
                            data-testid="add-tug-job"
                          >
                            {addingJob && <span className="spinner-border spinner-border-sm me-1" aria-hidden="true" />}
                            <i className="fas fa-plus me-1" aria-hidden="true" />
                            Add another {planning.jobType} job (one more tug)
                          </button>
                        )}
                      </div>
                    )}

                    {planError && (
                      <div className="alert alert-danger" role="alert" data-testid="plan-error">
                        {planError}
                      </div>
                    )}
                    {planMsg && (
                      <div className="alert alert-info py-2 small" role="status" data-testid="plan-message">
                        {planMsg}
                      </div>
                    )}

                    {/* 8953: the job's own date/time, editable before dispatch */}
                    <div className="mb-3">
                      <label htmlFor="d-schedule" className="form-label small fw-bold text-muted">
                        Job Scheduled Date/Time <span className="text-danger">*</span>
                      </label>
                      <input
                        id="d-schedule"
                        type="datetime-local"
                        className="form-control form-control-sm"
                        value={scheduledAt}
                        onChange={(e) => changeSchedule(e.target.value)}
                        data-testid="job-schedule"
                      />
                      <small className="text-muted">Tugs and captains below are checked for this time.</small>
                    </div>

                    <div className="mb-3">
                      <label htmlFor="d-tug" className="form-label small fw-bold text-muted">
                        Tug for this Job <span className="text-danger">*</span>
                      </label>
                      <select
                        id="d-tug"
                        className="form-select form-select-sm"
                        value={tugId}
                        onChange={(e) => changeTug(e.target.value)}
                        disabled={!planning}
                        data-testid="assign-tug"
                      >
                        <option value="">Select an available tug…</option>
                        {/* 8878: unavailable tugs are left out, not just disabled. */}
                        {tugs
                          .filter((t) => availability[t.id]?.available !== false || t.id === tugId)
                          .map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name} ({t.class_type} · {Number(t.bollard_pull_tons)}T · {t.operational_status})
                            </option>
                          ))}
                      </select>
                    </div>

                    {/* 8925 / 8953: the captain comes from the Crew Roster for the tug */}
                    <div className="mb-3" data-testid="tug-captains">
                      <label htmlFor="d-captain" className="form-label small fw-bold text-muted">
                        Captain
                      </label>
                      <select
                        id="d-captain"
                        className="form-select form-select-sm"
                        value={captainId}
                        onChange={(e) => {
                          setCaptainId(e.target.value);
                          setCaptainAuto(false);
                        }}
                        disabled={!tugId}
                        data-testid="tug-captain"
                      >
                        <option value="">— No captain —</option>
                        {assignableCaptains.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name} — Certified
                          </option>
                        ))}
                      </select>
                      {ownCaptain && ownCaptain.id === captainId && (
                        <small className="text-muted d-block mt-1" data-testid="tug-captain-source">
                          From the Crew Roster: {CAPTAIN_SOURCE[ownCaptain.source] || 'assigned to this tug'}
                        </small>
                      )}
                      {ownCaptain && !ownCaptain.available && (
                        <small className="text-danger d-block mt-1" data-testid="tug-captain-unavailable">
                          {ownCaptain.name} is unavailable: {ownCaptain.unavailableReason}
                        </small>
                      )}
                      {tugId && !ownCaptain && (
                        <small className="text-muted d-block mt-1" data-testid="tug-captain-none">
                          No captain is rostered on this tug that day — pick one if needed.
                        </small>
                      )}
                    </div>

                    {warnings.length > 0 && (
                      <div className="alert alert-warning py-2 small" data-testid="advisory-warning">
                        <i className="fas fa-triangle-exclamation me-1" aria-hidden="true" />
                        {warnings.join(' ')}
                      </div>
                    )}

                    <div className="form-check form-switch mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="d-override"
                        checked={override}
                        onChange={(e) => setOverride(e.target.checked)}
                        data-testid="override-toggle"
                      />
                      <label className="form-check-label small fw-bold text-muted" htmlFor="d-override">
                        Override Advisory Limits
                      </label>
                    </div>

                    {override && (
                      <div className="mb-3">
                        <label htmlFor="d-justification" className="form-label small fw-bold text-muted">
                          Rule Override Rationale <span className="text-danger">*</span>
                        </label>
                        <textarea
                          id="d-justification"
                          rows={2}
                          className="form-control form-control-sm"
                          value={justification}
                          onChange={(e) => setJustification(e.target.value)}
                          data-testid="override-justification"
                          placeholder="Operational or thruster-deduction reason…"
                        />
                      </div>
                    )}

                    <div className="d-flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="btn btn-outline-primary flex-fill"
                        onClick={saveAssignment}
                        disabled={savingDraft || dispatching}
                        data-testid="save-assignment"
                      >
                        {savingDraft && (
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        )}
                        <i className="fas fa-floppy-disk me-1" aria-hidden="true" />
                        Save Assignment
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary flex-fill"
                        disabled={dispatching || savingDraft}
                        data-testid="dispatch-btn"
                      >
                        {dispatching && (
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        )}
                        <i className="fas fa-ticket-alt me-1" aria-hidden="true" />
                        Trigger Dispatch Job Ticket
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dispatched job tickets — delivery + acknowledgment SLA monitoring */}
      {!loading && (
        <div className="card border-0 shadow-sm" data-testid="job-tickets">
          <div className="card-header bg-white pt-4 border-bottom-0">
            <h5 className="mb-0 text-secondary">
              <i className="fas fa-ticket-alt me-2" aria-hidden="true" />
              Dispatched Job Tickets
            </h5>
            <p className="small text-muted mb-0 mt-1" data-testid="ack-summary">
              Awaiting tug captain:{' '}
              <strong>{tickets.filter((t) => t.ack_status !== 'Acknowledged' && ticketOpen(t)).length}</strong>
              {' · '}
              Awaiting ship&apos;s master: <strong>{tickets.filter((t) => t.master_status === 'Awaiting').length}</strong>
            </p>
          </div>
          <div className="card-body p-0">
            {ticketMsg && (
              <div className={`alert alert-${ticketMsg.type} m-3`} role="alert" data-testid="ticket-message">
                {ticketMsg.text}
              </div>
            )}
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light text-secondary small text-uppercase">
                  <tr>
                    <th>Ticket / Job</th>
                    <th>Scheduled</th>
                    <th>Tug</th>
                    <th>Captain</th>
                    <th>Status</th>
                    <th>Tug captain</th>
                    <th>Elapsed</th>
                    <th>SLA</th>
                    <th>Ship&apos;s master</th>
                    <th aria-label="action" />
                  </tr>
                </thead>
                <tbody>
                  {tickets.length === 0 && (
                    <tr>
                      <td colSpan={10} className="text-center text-muted py-4">
                        No job tickets dispatched yet.
                      </td>
                    </tr>
                  )}
                  {/* 8989: the booking once, with a ticket row per job beneath it */}
                  {groupByBooking(tickets).map((g) => {
                    const collapsed = isTicketCollapsed(g.key);
                    // 9033: a cancelled job no longer counts towards the booking.
                    const live = g.rows.filter((t) => t.ticket_status !== 'Cancelled');
                    const open = live.filter((t) => t.ticket_status !== 'Completed').length;
                    const cancelled = g.rows.length - live.length;
                    const awaitingMaster = g.rows.filter((t) => t.master_status === 'Awaiting').length;
                    return (
                      <React.Fragment key={g.key}>
                        <BookingGroupRow group={g} colSpan={10} collapsed={collapsed} onToggle={toggleTicketGroup}>
                          <span data-testid="booking-group-detail">
                            {g.first.service_name ? `${g.first.service_name} · ` : ''}
                            {g.first.trading_status ? `${g.first.trading_status} · ` : ''}
                            {live.length - open} of {live.length} completed
                            {cancelled ? ` · ${cancelled} cancelled` : ''}
                            {g.rows.some((t) => t.overdue) ? ' · SLA breached' : ''}
                            {awaitingMaster ? ` · ${awaitingMaster} awaiting ship's master` : ''}
                          </span>
                          {bookingOpen(g.first.booking_status) && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary ms-2 py-0"
                              onClick={() => openAddTug(g.first.booking_id, g.first.booking_ref)}
                              data-testid="ticket-add-tug"
                            >
                              <i className="fas fa-plus me-1" aria-hidden="true" />
                              Add Tug
                            </button>
                          )}
                        </BookingGroupRow>
                        {!collapsed && g.rows.map((t) => (
                    <tr
                      key={t.id}
                      className={t.overdue ? 'ticket-overdue' : ''}
                      data-testid="ticket-row"
                      data-id={t.id}
                      data-ref={t.booking_ref}
                    >
                      <td className="fw-bold ps-4">
                        {t.ticket_no != null && (
                          <span className="d-block" data-testid="ticket-no">
                            Ticket No. {t.ticket_no}
                          </span>
                        )}
                        {/* 8954: the job this ticket is for (1 job = 1 tug) */}
                        {t.job_no && (
                          <small className="d-block text-muted fw-normal" data-testid="ticket-job">
                            {t.job_no} · {t.job_name}
                          </small>
                        )}
                      </td>
                      <td className="small" data-testid="ticket-scheduled">
                        {fmtWhen(t.scheduled_at)}
                      </td>
                      <td data-testid="ticket-tug">{t.tug_name || '—'}</td>
                      <td>
                        <span data-testid="ticket-captain">{t.captain_name || '—'}</span>
                        {t.captain_id && t.ticket_status !== 'Cancelled' && showEmailStatus(t) && (
                          <small
                            className={`d-block ${t.captain_email_status === 'failed' ? 'text-danger' : 'text-muted'}`}
                            data-testid="ticket-email-status"
                            data-status={t.captain_email_status || 'pending'}
                          >
                            <i className="fas fa-envelope me-1" aria-hidden="true" />
                            {emailStatusText(t)}
                          </small>
                        )}
                      </td>
                      <td>
                        <span
                          className={`badge ${TICKET_STATUS_BADGE[t.ticket_status] || 'bg-light text-dark border'}`}
                          data-testid="ticket-lifecycle"
                        >
                          {t.ticket_status}
                        </span>
                        {t.ticket_status === 'Cancelled' && t.cancel_reason && (
                          <small className="d-block text-muted" data-testid="ticket-cancel-reason">
                            {t.cancel_reason}
                          </small>
                        )}
                      </td>
                      <td>
                        <span
                          className={`badge ${t.ack_status === 'Acknowledged' ? 'bg-success' : 'bg-info text-dark'}`}
                          data-testid="ticket-status"
                        >
                          {t.delivery_label}
                        </span>
                        {t.ack_status === 'Acknowledged' && t.acknowledged_by && (
                          <small className="d-block text-muted" data-testid="ticket-ack-by">
                            {t.acknowledged_via === 'office' ? 'Recorded by ' : 'By '}
                            {t.acknowledged_by}
                          </small>
                        )}
                      </td>
                      <td data-testid="ticket-elapsed">{t.response_minutes ?? t.elapsed_minutes} min</td>
                      <td>
                        {t.overdue ? (
                          <span className="badge bg-danger" data-testid="ticket-overdue">
                            <i className="fas fa-triangle-exclamation me-1" aria-hidden="true" />
                            {t.ack_late
                              ? `Acknowledged late (${t.response_minutes}m > SLA ${t.sla_minutes}m)`
                              : 'SLA breached — SMS sent'}
                          </span>
                        ) : (
                          <span className="badge bg-light text-dark border">Within SLA ({t.sla_minutes}m)</span>
                        )}
                      </td>
                      <td data-testid="ticket-master">
                        {t.master_status === 'Confirmed' ? (
                          <>
                            <span className="badge bg-success">Confirmed</span>
                            <small className="d-block text-muted">
                              {t.assist_confirmed_by}
                              {t.assist_confirmed_via === 'office' ? ' · recorded by office' : ''}
                            </small>
                          </>
                        ) : t.master_status === 'Awaiting' ? (
                          <span className="badge bg-warning text-dark">Awaiting confirmation</span>
                        ) : (
                          <span className="text-muted small">After the assist</span>
                        )}
                      </td>
                      <td className="text-end text-nowrap">
                        {t.master_status === 'Awaiting' && (
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => openMaster(t)}
                            data-testid="record-master-confirmation"
                          >
                            <i className="fas fa-file-signature me-1" aria-hidden="true" />
                            Record Confirmation
                          </button>
                        )}
                        {t.ack_status !== 'Acknowledged' && ticketOpen(t) && (
                          <button
                            className="btn btn-sm btn-outline-success me-1"
                            onClick={() => markAcknowledged(t)}
                            data-testid="mark-acknowledged"
                          >
                            <i className="fas fa-check me-1" aria-hidden="true" />
                            Mark Acknowledged
                          </button>
                        )}
                        {t.ack_status !== 'Acknowledged' && ticketOpen(t) && (
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => openReassign(t)}
                            data-testid="manual-reassign"
                          >
                            <i className="fas fa-exchange-alt me-1" aria-hidden="true" />
                            Manual Reassign
                          </button>
                        )}
                        {/* 9033: swap the tug and captain, or cancel this one job */}
                        {ticketReplaceable(t) && (
                          <button
                            className="btn btn-sm btn-outline-primary ms-1"
                            onClick={() => openReplace(t)}
                            data-testid="replace-tug"
                          >
                            <i className="fas fa-right-left me-1" aria-hidden="true" />
                            Replace Tug
                          </button>
                        )}
                        {ticketCancellable(t) && (
                          <button
                            className="btn btn-sm btn-outline-danger ms-1"
                            onClick={() =>
                              openCancel({
                                jobId: t.booking_job_id,
                                jobNo: t.job_no,
                                jobName: t.job_name,
                                bookingRef: t.booking_ref,
                                ticketNo: t.ticket_no,
                              })
                            }
                            data-testid="cancel-job"
                          >
                            <i className="fas fa-ban me-1" aria-hidden="true" />
                            Cancel Job
                          </button>
                        )}
                      </td>
                    </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* 9027: Job Summary confirmed before the ticket is sent */}
      <ConfirmModal
        open={summaryOpen && Boolean(selected)}
        testId="dispatch-summary"
        title="Confirm dispatch — Job Summary"
        icon="fa-clipboard-check text-primary"
        variant="primary"
        size="lg"
        confirmLabel="Confirm & Dispatch"
        busy={dispatching}
        onCancel={() => setSummaryOpen(false)}
        onConfirm={confirmDispatch}
        message={
          selected && (
            <div className="small">
              <dl className="row mb-3">
                <dt className="col-sm-4 text-muted fw-normal">Booking</dt>
                <dd className="col-sm-8 fw-bold mb-1" data-testid="summary-booking">{selected.booking_ref}</dd>
                <dt className="col-sm-4 text-muted fw-normal">Customer</dt>
                <dd className="col-sm-8 fw-bold mb-1" data-testid="summary-customer">{selected.customer_name || '—'}</dd>
                <dt className="col-sm-4 text-muted fw-normal">Vessel</dt>
                <dd className="col-sm-8 fw-bold mb-1" data-testid="summary-vessel">{selected.vessel_name || '—'}</dd>
                <dt className="col-sm-4 text-muted fw-normal">Service</dt>
                <dd className="col-sm-8 fw-bold mb-1" data-testid="summary-service">
                  {selected.service_name || selected.movement_type || '—'}
                </dd>
                <dt className="col-sm-4 text-muted fw-normal">Port / Area / Berth</dt>
                <dd className="col-sm-8 fw-bold mb-1" data-testid="summary-location">
                  {selected.port_name || '—'} / {selected.assistance_area_name || '—'} / {selected.berth_name || '—'}
                </dd>
                <dt className="col-sm-4 text-muted fw-normal">Scheduled (24h)</dt>
                <dd className="col-sm-8 fw-bold mb-1" data-testid="summary-time">
                  {fmtWhen(scheduledAt ? new Date(scheduledAt) : selected.scheduled_at)}
                </dd>
              </dl>
              <table className="table table-sm align-middle mb-2" data-testid="summary-jobs">
                <thead className="bg-light text-secondary text-uppercase">
                  <tr>
                    <th>Job</th>
                    <th>Tug</th>
                    <th>Captain</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryJobs.map((j) => (
                    <tr
                      key={j.id}
                      className={j.current ? 'table-primary' : ''}
                      data-testid="summary-job"
                      data-job-no={j.job_no}
                      data-current={j.current ? 'true' : 'false'}
                    >
                      <td>
                        <span className="fw-bold d-block">{j.job_no}</span>
                        <span className="text-muted">{j.job_name}</span>
                      </td>
                      <td data-testid="summary-job-tug">{j.tug_name || '—'}</td>
                      <td data-testid="summary-job-captain">{j.captain_name || '—'}</td>
                      <td>
                        <span className={`badge ${j.current ? 'bg-primary' : 'bg-light text-dark border'}`}>{j.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {warnings.length > 0 && override && (
                <div className="alert alert-warning py-2 mb-0" data-testid="summary-override">
                  Advisory overridden: {justification.trim()}
                </div>
              )}
              <p className="text-muted mb-0 mt-2">
                Confirm sends the job ticket for <strong>{selected.job_no}</strong> to the tug
                {captainId ? ' and emails its captain' : ''}. Cancel returns to the planning desk.
              </p>
            </div>
          )
        }
      />

      {/* 9033: Add Tug — one more job (one more tug) on the booking */}
      <ConfirmModal
        open={Boolean(addTugTarget)}
        testId="add-tug-modal"
        title={`Add Tug — ${addTugTarget?.bookingRef || ''}`}
        icon="fa-plus text-primary"
        variant="primary"
        confirmLabel="Add Tug"
        busy={jobActionBusy}
        error={addTugError}
        onCancel={() => setAddTugTarget(null)}
        onConfirm={submitAddTug}
        message={
          addTugTarget && (
            <div className="small">
              <p className="text-muted">
                Adds one more job — one more tug — to this booking. It is planned and dispatched from the Confirmed
                Job Queue like the booking&apos;s other jobs, and billed with them.
              </p>
              <div className="mb-3">
                <label htmlFor="at-like" className="form-label fw-bold text-muted">
                  Extra tug for <span className="text-danger">*</span>
                </label>
                <select
                  id="at-like"
                  className="form-select form-select-sm"
                  value={addTugLike}
                  onChange={(e) => setAddTugLike(e.target.value)}
                  data-testid="add-tug-like"
                >
                  {addTugTarget.jobs.length === 0 && <option value="">No live job on this booking</option>}
                  {addTugTarget.jobs.map((j) => (
                    <option key={j.id} value={j.id}>
                      {j.type} (like {j.label})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="at-reason" className="form-label fw-bold text-muted">
                  Reason
                </label>
                <textarea
                  id="at-reason"
                  rows={2}
                  className="form-control form-control-sm"
                  value={addTugReason}
                  maxLength={1000}
                  onChange={(e) => setAddTugReason(e.target.value)}
                  placeholder="e.g. Strong winds — the master asked for an extra tug"
                  data-testid="add-tug-reason"
                />
              </div>
            </div>
          )
        }
      />

      {/* 9033: Replace Tug — another available tug (and its captain) takes the job */}
      <ConfirmModal
        open={Boolean(replaceTarget)}
        testId="replace-tug-modal"
        title={`Replace Tug — ${replaceTarget?.booking_ref || ''} · ${replaceTarget?.job_no || ''}`}
        icon="fa-right-left text-primary"
        variant="primary"
        confirmLabel="Replace Tug"
        busy={jobActionBusy}
        error={replaceError}
        onCancel={() => setReplaceTarget(null)}
        onConfirm={submitReplace}
        message={
          replaceTarget && (
            <div className="small">
              <p className="text-muted">
                Currently <strong>{replaceTarget.tug_name || '—'}</strong> with{' '}
                <strong>{replaceTarget.captain_name || 'no captain named'}</strong>, scheduled{' '}
                {fmtWhen(replaceTarget.scheduled_at)}. The ticket moves to the new tug and captain, who must acknowledge
                it again; the previous captain no longer sees it.
              </p>
              {!replaceOptions ? (
                !replaceError && <div className="text-muted py-2">Checking which tugs are free then…</div>
              ) : (
                <>
                  <div className="mb-3">
                    <label htmlFor="rt-tug" className="form-label fw-bold text-muted">
                      Replacement Tug <span className="text-danger">*</span>
                    </label>
                    <select
                      id="rt-tug"
                      className="form-select form-select-sm"
                      value={replaceTugId}
                      onChange={(e) => changeReplaceTug(e.target.value)}
                      data-testid="replace-tug-select"
                    >
                      <option value="">Select an available tug…</option>
                      {replaceOptions.tugs
                        .filter((t) => t.available)
                        .map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name} ({t.bollardPullTons}T){t.captain?.available ? ` — ${t.captain.name}` : ''}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="rt-captain" className="form-label fw-bold text-muted">
                      Captain
                    </label>
                    <select
                      id="rt-captain"
                      className="form-select form-select-sm"
                      value={replaceCaptainId}
                      onChange={(e) => setReplaceCaptainId(e.target.value)}
                      disabled={!replaceTugId}
                      data-testid="replace-captain-select"
                    >
                      <option value="">— No captain —</option>
                      {replaceOptions.captains
                        .filter((c) => c.available)
                        .map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                    </select>
                  </div>
                </>
              )}
              <div>
                <label htmlFor="rt-reason" className="form-label fw-bold text-muted">
                  Reason <span className="text-danger">*</span>
                </label>
                <textarea
                  id="rt-reason"
                  rows={2}
                  className="form-control form-control-sm"
                  value={replaceReason}
                  maxLength={1000}
                  onChange={(e) => setReplaceReason(e.target.value)}
                  placeholder="e.g. Engine fault on the original tug"
                  data-testid="replace-tug-reason"
                />
              </div>
            </div>
          )
        }
      />

      {/* 9033: Cancel Job — one job of the booking, with a reason */}
      <ConfirmModal
        open={Boolean(cancelTarget)}
        testId="cancel-job-modal"
        title={`Cancel Job — ${cancelTarget?.bookingRef || ''} · ${cancelTarget?.jobNo || ''}`}
        confirmLabel="Cancel Job"
        busy={jobActionBusy}
        error={cancelError}
        onCancel={() => setCancelTarget(null)}
        onConfirm={submitCancel}
        message={
          cancelTarget && (
            <div className="small">
              <p className="text-muted">
                Cancels <strong>{cancelTarget.jobName}</strong>
                {cancelTarget.ticketNo != null ? ` and its ticket No. ${cancelTarget.ticketNo}` : ''}. It leaves the
                captain&apos;s console and is not billed; the booking&apos;s other jobs go ahead.
              </p>
              <label htmlFor="cj-reason" className="form-label fw-bold text-muted">
                Cancellation Reason <span className="text-danger">*</span>
              </label>
              <textarea
                id="cj-reason"
                rows={2}
                className="form-control form-control-sm"
                value={cancelReason}
                maxLength={1000}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="e.g. The master no longer needs a second tug"
                data-testid="cancel-job-reason"
              />
            </div>
          )
        }
      />

      {/* Ship's master confirmation, recorded by the office */}
      {masterTarget && (
        <>
          <div className="modal d-block" tabIndex={-1} role="dialog" data-testid="master-modal">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <form onSubmit={submitMaster} noValidate>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      <i className="fas fa-file-signature me-2 text-primary" aria-hidden="true" />
                      Assist confirmation — {masterTarget.booking_ref}{masterTarget.job_no ? ` · ${masterTarget.job_no}` : ''}
                    </h5>
                    <button type="button" className="btn-close" aria-label="Close" onClick={() => setMasterTarget(null)} />
                  </div>
                  <div className="modal-body">
                    {masterError && (
                      <div className="alert alert-danger" role="alert" data-testid="master-error">
                        {masterError}
                      </div>
                    )}
                    <p className="small text-muted">
                      The ship&apos;s master has not confirmed this job ({masterTarget.ticket_no}) on their own login.
                      Record it here once you have their confirmation another way. The booking&apos;s other jobs are
                      confirmed separately.
                    </p>
                    <div className="mb-3">
                      <label htmlFor="m-name" className="form-label small fw-bold text-muted">
                        Confirmed by (master or agent) <span className="text-danger">*</span>
                      </label>
                      <input
                        id="m-name"
                        className="form-control form-control-sm"
                        value={masterName}
                        maxLength={160}
                        onChange={(e) => setMasterName(e.target.value)}
                        data-testid="master-name"
                      />
                    </div>
                    <div className="mb-1">
                      <label htmlFor="m-note" className="form-label small fw-bold text-muted">
                        How it was received <span className="text-danger">*</span>
                      </label>
                      <textarea
                        id="m-note"
                        rows={2}
                        className="form-control form-control-sm"
                        value={masterNote}
                        maxLength={1000}
                        onChange={(e) => setMasterNote(e.target.value)}
                        data-testid="master-note"
                        placeholder="e.g. Signed paper ticket, phone call from the agent…"
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setMasterTarget(null)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={masterSaving} data-testid="confirm-master">
                      {masterSaving && (
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      )}
                      Record Confirmation
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" />
        </>
      )}

      {/* Manual reassignment modal */}
      {reassignTarget && (
        <>
          <div className="modal d-block" tabIndex={-1} role="dialog" data-testid="reassign-modal">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <form onSubmit={submitReassign} noValidate>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      <i className="fas fa-exchange-alt me-2 text-primary" aria-hidden="true" />
                      Manual Reassign — {reassignTarget.booking_ref}
                      {reassignTarget.job_no ? ` · ${reassignTarget.job_no}` : ''}
                    </h5>
                    <button type="button" className="btn-close" aria-label="Close" onClick={() => setReassignTarget(null)} />
                  </div>
                  <div className="modal-body">
                    {reassignError && (
                      <div className="alert alert-danger" role="alert" data-testid="reassign-error">
                        {reassignError}
                      </div>
                    )}
                    <p className="small text-muted">
                      Currently assigned to <strong>{reassignTarget.tug_name || '—'}</strong>.
                    </p>
                    <div className="mb-3">
                      <label htmlFor="r-tug" className="form-label small fw-bold text-muted">
                        Alternative Tug <span className="text-danger">*</span>
                      </label>
                      <select
                        id="r-tug"
                        className="form-select form-select-sm"
                        value={reassignTug}
                        onChange={(e) => setReassignTug(e.target.value)}
                        data-testid="reassign-tug"
                      >
                        <option value="">Select a tug…</option>
                        {tugs
                          // Not the current tug, nor one already on another ticket of this booking
                          // at an overlapping time (8860) — a tug may dock a vessel and later undock it.
                          .filter(
                            (t) =>
                              t.id !== reassignTarget.tug_id &&
                              !tickets.some(
                                (k) =>
                                  k.id !== reassignTarget.id &&
                                  k.booking_id === reassignTarget.booking_id &&
                                  k.tug_id === t.id &&
                                  k.status !== 'Cancelled' &&
                                  Math.abs(new Date(k.scheduled_at) - new Date(reassignTarget.scheduled_at)) <= 3 * 3_600_000
                              )
                          )
                          .map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name} ({Number(t.bollard_pull_tons)}T)
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="mb-1">
                      <label htmlFor="r-reason" className="form-label small fw-bold text-muted">
                        Reassignment Rationale <span className="text-danger">*</span>
                      </label>
                      <textarea
                        id="r-reason"
                        rows={2}
                        className="form-control form-control-sm"
                        value={reassignReason}
                        onChange={(e) => setReassignReason(e.target.value)}
                        data-testid="reassign-reason"
                        placeholder="e.g. Captain non-responsive past SLA…"
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setReassignTarget(null)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={reassigning} data-testid="confirm-reassign">
                      {reassigning && (
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      )}
                      <i className="fas fa-check me-1" aria-hidden="true" />
                      Confirm Reassignment
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}

      {/* Broadcast to Affiliates modal */}
      {broadcastBooking && (
        <>
          <div className="modal d-block" tabIndex={-1} role="dialog" data-testid="broadcast-modal">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <form onSubmit={submitBroadcast} noValidate>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      <i className="fas fa-satellite-dish me-2 text-primary" aria-hidden="true" />
                      Broadcast to Affiliates — {broadcastBooking.booking_ref}
                    </h5>
                    <button type="button" className="btn-close" aria-label="Close" onClick={() => setBroadcastBooking(null)} />
                  </div>
                  <div className="modal-body">
                    {broadcastError && (
                      <div className="alert alert-danger" role="alert" data-testid="broadcast-error">
                        {broadcastError}
                      </div>
                    )}
                    {!eligible ? (
                      <div className="text-center text-muted py-3">Finding eligible affiliates…</div>
                    ) : (
                      <>
                        <p className="small text-muted">
                          Required bollard pull: <strong>{eligible.requiredBollard} T</strong>. Eligible operators
                          (by tug size &amp; availability):
                        </p>
                        {eligible.operators.length === 0 ? (
                          <div className="alert alert-warning py-2" data-testid="no-eligible">
                            No eligible affiliate operators found.
                          </div>
                        ) : (
                          eligible.operators.map((o) => (
                            <div className="form-check" key={o.operatorId}>
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`op-${o.operatorId}`}
                                checked={selectedOperators.includes(o.operatorId)}
                                onChange={() => toggleOperator(o.operatorId)}
                                data-testid={`broadcast-operator-${o.operatorId}`}
                              />
                              <label className="form-check-label small" htmlFor={`op-${o.operatorId}`}>
                                {o.companyName} — best tug {o.bestBollard} T
                                {o.dispatchPhone ? ` · ${o.dispatchPhone}` : ''}
                              </label>
                            </div>
                          ))
                        )}
                      </>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setBroadcastBooking(null)}>
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary btn-sm"
                      disabled={broadcasting || !eligible || eligible.operators.length === 0}
                      data-testid="confirm-broadcast"
                    >
                      {broadcasting && (
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      )}
                      <i className="fas fa-bullhorn me-1" aria-hidden="true" />
                      Broadcast
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  );
}
